import { useEffect } from 'react';
import './DropdownScroll.scss';

/** Popup class that enables scroll feedback styles (see DropdownScroll.scss). */
export const SCROLL_FEEDBACK_CLASS = 'scroll-feedback-dropdown';

// Scrollable lists inside any open dropdown: Select/AutoComplete holder or Dropdown menu.
const LIST_SELECTOR = '.ant-select-dropdown .rc-virtual-list-holder, .ant-dropdown .ant-dropdown-menu';

const getScrollableList = (target: EventTarget | null): HTMLElement | null => (
  target instanceof Element ? target.closest(LIST_SELECTOR) as HTMLElement | null : null
);

const isAtTop = (list: HTMLElement) => list.scrollTop <= 0;
const isAtBottom = (list: HTMLElement) => list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

/**
 * While an antd dropdown (Select, AutoComplete or Dropdown menu) is open, only its option list may scroll.
 * Touch/wheel gestures outside the list are cancelled, and gestures inside the
 * list are cancelled when it is already at its top/bottom edge, so the page
 * never scrolls behind the dropdown (incl. iOS versions without overscroll-behavior).
 * Select/AutoComplete must use native scroll (virtual={false}).
 */
export function useDropdownScrollLock(isOpen: boolean) {
  useEffect(() => {
    if (!isOpen) return;

    let lastTouchY = 0;

    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0]?.clientY ?? 0;
    };

    const onTouchMove = (event: TouchEvent) => {
      const currentY = event.touches[0]?.clientY ?? lastTouchY;
      const deltaY = currentY - lastTouchY; // > 0: finger moves down (content scrolls up)
      lastTouchY = currentY;

      const list = getScrollableList(event.target);
      const shouldBlock = !list || (deltaY > 0 && isAtTop(list)) || (deltaY < 0 && isAtBottom(list));

      if (shouldBlock && event.cancelable) {
        event.preventDefault();
      }
    };

    const onWheel = (event: WheelEvent) => {
      const list = getScrollableList(event.target);
      const shouldBlock = !list || (event.deltaY < 0 && isAtTop(list)) || (event.deltaY > 0 && isAtBottom(list));

      if (shouldBlock && event.cancelable) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    document.addEventListener('wheel', onWheel, { passive: false, capture: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
      document.removeEventListener('wheel', onWheel, { capture: true });
    };
  }, [isOpen]);
}
